import React, { useEffect, useState, useMemo, useCallback, memo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { FormControl } from '@mui/material'
import { ToolTip, OutlinedButton } from 'irmlib/components/Controls'
import { CheckboxMultiselect } from '../../../irmlib/src/components/FormControls/Filter'
import { YDivider, useTheme, FlexByRow, HalfScreen } from '../../../irmlib/src/styles'
import { MainRouteSubRoutePage } from '../../AppControl/RoutePage'
import { useSearchParams } from '../../../irmlib/src/hooks'
import { MaterialReactTable } from 'material-react-table'
import * as routePaths from '../../../navigation/routePaths'
import * as gdeTransactionsLite from '../../../services/firestore/gdeTransactionsLite'
import * as constants from 'irmlib/common/constants'
import { errorToast } from '../../../common/functions'
import { getUser, persistFilters } from '../../../redux/global'
import { totalNoOfBoxesForJob } from '../../../services/common/appConstants'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import ExportCsv from '../../Controls/ExportCsv'

const CREATED_DATE_FORMAT = 'YYYY-MM-DD'
const CREATED_DATE_EMPTY = '0000-00-00'

const formatCreatedDateSortValue = (createdTs) => {
  if (!createdTs?.seconds) return CREATED_DATE_EMPTY
  const formatted = moment(createdTs.seconds, 'X').format(CREATED_DATE_FORMAT)
  return formatted === 'Invalid date' ? CREATED_DATE_EMPTY : formatted
}

const formatCreatedDateDisplay = (dateCreated) => {
  if (!dateCreated || dateCreated === CREATED_DATE_EMPTY) return '—'
  const m = moment(dateCreated, CREATED_DATE_FORMAT, true)
  return m.isValid() ? m.format(CREATED_DATE_FORMAT) : '—'
}

const BottomPaddedDiv = styled('div')`
  padding-bottom: 20px;
`

const TableContainer = styled(BottomPaddedDiv)`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
`

const JobLink = styled(Link)`
  color: ${props => props.theme.palette.primary.main};
  font-weight: 400;
  text-decoration: none;
`

const showUsers = (obj) => {
  const names = []
  if (!obj || typeof obj !== 'object') return ''
  for (const key in obj) {
    const u = obj[key]
    if (u?.workerName !== undefined && Array.isArray(u?.jobCategory) && u.jobCategory.includes('Data Entry User')) {
      names.push(u.workerName)
    }
  }
  return names.join(',')
}

const QC_JOBS_CACHE_TTL_MS = 2 * 60 * 1000
const DISTRICTS_CACHE_TTL_MS = 10 * 60 * 1000
const MAX_DISTRICT_SELECTION = 7

let qcJobsCache = { key: '', rows: [], fetchedAt: 0 }
let districtsListCache = { key: '', list: [], fetchedAt: 0 }

const districtsCacheKey = (country) => country || '__all__'

const getCachedDistrictsList = (country) => {
  const key = districtsCacheKey(country)
  if (districtsListCache.key === key && Date.now() - districtsListCache.fetchedAt < DISTRICTS_CACHE_TTL_MS) {
    return districtsListCache.list
  }
  return null
}

const setCachedDistrictsList = (country, list) => {
  districtsListCache = {
    key: districtsCacheKey(country),
    list,
    fetchedAt: Date.now(),
  }
}

const mapDistrictDocsToOptions = (collection) => {
  const list = []
  collection?.forEach((doc) => {
    list.push({ value: doc.data().districtName, label: doc.data().districtName })
  })
  return list
}

const buildJobsCacheKey = (filterVal, defaultDistrict) =>
  JSON.stringify({
    districts: filterVal.districts,
    jobStatus: filterVal.jobStatus,
    jobId: filterVal.jobId,
    customer: filterVal.customer,
    templateNAme: filterVal.templateNAme,
    deType: filterVal.deType,
    userName: filterVal.userName,
    defaultDistrict,
  })

const getCachedQcJobs = (filterVal, defaultDistrict) => {
  const key = buildJobsCacheKey(filterVal, defaultDistrict)
  if (qcJobsCache.key === key && Date.now() - qcJobsCache.fetchedAt < QC_JOBS_CACHE_TTL_MS) {
    return qcJobsCache.rows
  }
  return null
}

const setCachedQcJobs = (filterVal, defaultDistrict, rows) => {
  qcJobsCache = {
    key: buildJobsCacheKey(filterVal, defaultDistrict),
    rows,
    fetchedAt: Date.now(),
  }
}

const mapJobDocsToRows = (jobDocs) => {
  const rows = []
  jobDocs.forEach((doc) => {
    let totalNoOfBoxes = 0
    if (doc.data().totalBoxes === totalNoOfBoxesForJob) {
      totalNoOfBoxes = '-'
    } else {
      totalNoOfBoxes = parseInt(doc.data().totalBoxes, 10)
    }

    const data = doc.data()
    const dateCreated = formatCreatedDateSortValue(data.createdTs)
    rows.push({
      jobId: doc.id,
      dataEntryUser: showUsers(data.assignedUsers),
      boxes: totalNoOfBoxes,
      boxesKeyed: data.boxesCompleted ? data.boxesCompleted : 0,
      status: data.jobStatus,
      status1: data.jobStatus,
      dataEntryType: data.dataEntryType || '',
      templateType: data.standardTemplateName || '',
      dateCreated,
      dateCreatedLabel: formatCreatedDateDisplay(dateCreated),
    })
  })
  return rows
}

/** QC boxes collection-group, then job docs by id only (not full district job scan). */
const fetchQcEligibleJobs = async (filterVal, defaultDistrict) => {
  const loggedInUserDistrict = getUser().district
  const rawDistricts = [...filterVal.districts].filter((district) => district !== constants.defaultSelect)
  const districtIds = defaultDistrict
    ? [...new Set([...rawDistricts, loggedInUserDistrict])]
    : (rawDistricts.length ? rawDistricts : [loggedInUserDistrict])

  const queryVal = { ...filterVal, districts: districtIds }
  const statusList = [constants.boxStatus.qcInProcess, constants.boxStatus.readyForQc]

  const boxGroupSnap = await gdeTransactionsLite.getQualityHubBoxesInQcStatuses(statusList, districtIds)

  const jobIdSet = new Set()
  boxGroupSnap.docs.forEach((doc) => {
    jobIdSet.add(doc.ref.parent.parent.id)
  })

  if (jobIdSet.size === 0) {
    return []
  }

  let jobDocs
  try {
    jobDocs = await gdeTransactionsLite.getQualityHubJobsForQCByJobIds(queryVal, [...jobIdSet])
  } catch {
    const jobsSnap = await gdeTransactionsLite.getJobsForQC(queryVal)
    jobDocs = jobsSnap.docs.filter((data) => jobIdSet.has(data.id))
  }

  return mapJobDocsToRows(jobDocs)
}

/** Sort key: real numbers only; empty / '-' / invalid → undefined (sorts last after 0, asc or desc). */
const boxesSortAccessor = (v) => {
  if (v === '-' || v === null || v === undefined || v === '') return undefined
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : undefined
}

const emptyServerFilter = {
  deType: constants.defaultSelect,
  templateNAme: constants.defaultSelect,
  jobId: constants.defaultSelect,
  customer: constants.defaultForAccountLevel,
  districts: [constants.defaultSelect],
  initialStatus: 'APPROVED',
  userName: constants.defaultSelect,
  createDateFrom: constants.defaultSelect,
  createDateTo: constants.defaultSelect,
}

const getDefaultDistrictSelection = () => {
  const { district } = persistFilters()
  const userDistrict = getUser().district
  if (district?.length) {
    return district
  }
  return userDistrict ? [userDistrict] : []
}

const DistrictFilterBar = memo(({
  districtsList,
  districtDefaultOption,
  districtLabel,
  onSelect,
  onSearch,
}) => (
  <FlexByRow>
    <HalfScreen>
      <FlexByRow>
        <CheckboxMultiselect
          options={districtsList}
          defaultOption={districtDefaultOption}
          label={districtLabel}
          handleSelect={onSelect}
          name="District"
        />
        <FormControl sx={{ m: 1, width: '90px', display: 'flex', justifyContent: 'center' }}>
          <OutlinedButton id="qualityHub_districtSearch" variant="contained" onClick={onSearch}>
            forms.Search
          </OutlinedButton>
        </FormControl>
      </FlexByRow>
    </HalfScreen>
  </FlexByRow>
))

const buildInitialFilterAndDistricts = (statusNavParam) => {
  const baseFilter = {
    ...emptyServerFilter,
    jobStatus: statusNavParam,
    initialStatus: statusNavParam === constants.defaultSelect ? 'NoApprove' : 'APPROVED',
  }
  const savedDistricts = getDefaultDistrictSelection()
  if (savedDistricts.length) {
    return {
      filterVal: { ...baseFilter, districts: [constants.defaultSelect, ...savedDistricts] },
      defaultDistrict: false,
    }
  }
  return {
    filterVal: baseFilter,
    defaultDistrict: true,
  }
}

const QualityHubJobs = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const location = useLocation()
  const { country, workerId, district } = getUser()
  const viewActivity = location.pathname.split('/')[1]
  const { selectedDatas } = location.state || {}
  const [districtsList, setDistrictsList] = useState(() => getCachedDistrictsList(country) || [])
  const [selectedValue, setSelectedValue] = useState({})
  const searchParams = useSearchParams()
  const statusNavParam = searchParams?.get('status') || constants.defaultSelect
  const initialDistrictState = useMemo(
    () => buildInitialFilterAndDistricts(statusNavParam),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once per statusNavParam
    [statusNavParam],
  )

  const [filterVal, setFilterVal] = useState(() => initialDistrictState.filterVal)
  const [defaultDistrict, setDefaultDistrict] = useState(() => initialDistrictState.defaultDistrict)

  const initialCachedJobs = getCachedQcJobs(initialDistrictState.filterVal, initialDistrictState.defaultDistrict)
  const [tableData, setTableData] = useState(() => initialCachedJobs || [])
  const [loading, setLoading] = useState(() => !initialCachedJobs)
  const [rowSelection, setRowSelection] = useState({})

  useEffect(() => {
    setRowSelection({})
  }, [tableData])

  useEffect(() => {
    setFilterVal((prev) => ({
      ...prev,
      jobStatus: statusNavParam,
      initialStatus: statusNavParam === constants.defaultSelect ? 'NoApprove' : 'APPROVED',
    }))
  }, [statusNavParam])

  useEffect(() => {
    let cancelled = false
    const cached = getCachedDistrictsList(country)
    if (cached) {
      setDistrictsList(cached)
      return () => { cancelled = true }
    }
    const loadDistricts = async () => {
      try {
        const collection = country
          ? await gdeTransactionsLite.getDistrictsByCountry(country)
          : await gdeTransactionsLite.getDistricts()
        const list = mapDistrictDocsToOptions(collection)
        setCachedDistrictsList(country, list)
        if (!cancelled) {
          setDistrictsList(list)
        }
      } catch {
        /* keep existing list */
      }
    }
    loadDistricts()
    return () => { cancelled = true }
  }, [country])

  const getSelectedDistrict = useCallback((data) => {
    if (data.District) {
      if (data.District.length) {
        if (data.District.length > MAX_DISTRICT_SELECTION) {
          errorToast({
            message: t('toast.districtSelection'),
            title: t('toast.failedToFetch'),
            autoHideDuration: 3000,
            isBlocking: false,
          })
        } else {
          setDefaultDistrict(false)
          setFilterVal((prev) => ({
            ...prev,
            districts: [constants.defaultSelect, ...data.District],
          }))
        }
      } else {
        errorToast({
          message: t('toast.invalidDistrict'),
          title: t('toast.error'),
          autoHideDuration: 3000,
          isBlocking: false,
        })
      }
    }
  }, [t])

  const defDistricts = useMemo(() => {
    const saved = persistFilters()?.district
    return saved?.length ? saved : (district ? [district] : [])
  }, [district])

  const finalDist = useMemo(
    () => (Object.keys(selectedValue).length === 0 ? defDistricts : selectedValue.District),
    [selectedValue, defDistricts],
  )

  const districtDefaultOption = useMemo(
    () => (selectedDatas ? [selectedDatas] : finalDist),
    [selectedDatas, finalDist],
  )

  const districtLabel = useMemo(() => t('Search.District'), [t])

  const handleSelectedData = useCallback((data) => {
    setSelectedValue(data)
  }, [])

  const handleDistrictSearch = useCallback(() => {
    const payload = Object.keys(selectedValue).length === 0
      ? { District: finalDist }
      : selectedValue
    getSelectedDistrict(payload)
    try {
      const storedData = localStorage.getItem('persistFilters')
      const existingData = storedData && storedData !== 'undefined' ? JSON.parse(storedData) : {}
      if (!existingData[viewActivity]) {
        existingData[viewActivity] = {}
      }
      existingData[viewActivity] = {
        workerId,
        country,
        district: payload.District || [district],
      }
      localStorage.setItem('persistFilters', JSON.stringify(existingData))
    } catch {
      /* ignore storage errors */
    }
  }, [country, district, finalDist, getSelectedDistrict, selectedValue, viewActivity, workerId])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const cached = getCachedQcJobs(filterVal, defaultDistrict)
      if (cached) {
        setTableData(cached)
        setLoading(false)
      } else {
        setLoading(true)
      }
      try {
        const rows = await fetchQcEligibleJobs(filterVal, defaultDistrict)
        if (!cancelled) {
          const nextRows = rows || []
          setTableData(nextRows)
          setCachedQcJobs(filterVal, defaultDistrict, nextRows)
        }
      } catch (err) {
        if (!cancelled) {
          if (!cached) {
            errorToast({ message: err?.message, title: t('toast.failedToFetch') })
            setTableData([])
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }
    load()
    return () => { cancelled = true }
  }, [filterVal, defaultDistrict, t])

  const exportColumns = useMemo(() => [
    { header: t('tableColumns.jobId'), accessorKey: 'jobId' },
    { header: t('tableColumns.status'), accessorKey: 'status' },
    { header: t('tableColumns.dateCreated'), accessorKey: 'dateCreatedLabel' },
    { header: t('Search.templateType'), accessorKey: 'dataEntryType' },
    { header: t('tableColumns.entryUser'), accessorKey: 'dataEntryUser' },
    { header: t('tableColumns.boxes'), accessorKey: 'boxes' },
    { header: t('tableColumns.boxesCompleted'), accessorKey: 'boxesKeyed' },
  ], [t])

  const mrtColumns = useMemo(() => [
    {
      accessorKey: 'jobId',
      header: t('tableColumns.jobId'),
      grow: 1.1,
      enableSorting: true,
      Cell: ({ row }) => (
        <JobLink
          id={`qualityHub_jobLink_${row.original.jobId}`}
          theme={theme}
          to={
            row.original.templateType === 'skpStandardNewFile' || row.original.templateType === 'skpStandardMaintenanceFile'
              ? `${routePaths.boxQualityCheck}/${row.original.jobId}`
              : `${routePaths.boxFlowQualityCheck}/${row.original.jobId}`
          }
        >
          {row.original.jobId}
        </JobLink>
      ),
    },
    {
      accessorKey: 'status',
      header: t('tableColumns.status'),
      grow: 0.8,
      enableSorting: true,
    },
    {
      accessorKey: 'dateCreated',
      header: t('tableColumns.dateCreated'),
      grow: 0.7,
      enableSorting: true,
      sortingFn: 'basic',
      Cell: ({ row }) => formatCreatedDateDisplay(row.original.dateCreated),
    },
    {
      accessorKey: 'dataEntryUser',
      header: t('tableColumns.entryUser'),
      grow: 1.8,
      enableSorting: true,
      Cell: ({ row }) => (
        <ToolTip
          id="qualityHub_entryUser_tip"
          noTranslate
          placement="bottom-start"
          message={row.original.dataEntryUser}
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', display: 'block' }}
        >
          {row.original.dataEntryUser}
        </ToolTip>
      ),
    },
    {
      id: 'boxes',
      accessorFn: (row) => boxesSortAccessor(row.boxes),
      header: t('tableColumns.boxes'),
      grow: 0.3,
      enableSorting: true,
      sortUndefined: 'last',
      sortingFn: 'basic',
      Cell: ({ row }) => row.original.boxes,
    },
    {
      id: 'boxesKeyed',
      accessorFn: (row) => boxesSortAccessor(row.boxesKeyed),
      header: t('tableColumns.boxesCompleted'),
      grow: 0.6,
      enableSorting: true,
      sortUndefined: 'last',
      sortingFn: 'basic',
      Cell: ({ row }) => row.original.boxesKeyed,
    },
    {
      accessorKey: 'dataEntryType',
      header: t('Search.templateType'),
      grow: 1.4,
      enableSorting: true,
      Cell: ({ row }) => {
        const v = row.original.dataEntryType
        if (!v) return '—'
        const translated = t(`templateTypeObj.${v}`)
        const label = typeof translated === 'string' && translated.includes('templateTypeObj.') ? v : translated
        return (
          <ToolTip id="qualityHub_templateType_tip" noTranslate placement="bottom-start" message={label}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', width: '100%' }}>
              {label}
            </span>
          </ToolTip>
        )
      },
    },
  ], [t, theme])

  const tableState = useMemo(() => ({
    rowSelection,
    isLoading: loading,
  }), [rowSelection, loading])

  return (
    <MainRouteSubRoutePage
      mainTitle='routeTitles.jobManagement'
      subTitle='routeTitles.jobReadyQualityCheck'
    >
      <YDivider height="8px" />
      <DistrictFilterBar
        districtsList={districtsList}
        districtDefaultOption={districtDefaultOption}
        districtLabel={districtLabel}
        onSelect={handleSelectedData}
        onSearch={handleDistrictSearch}
      />
      <YDivider height="15px" />
      <TableContainer>
        <MaterialReactTable
          columns={mrtColumns}
          data={tableData}
          enableStickyHeader
          enableRowVirtualization={false}
          defaultColumn={{
            minSize: 72,
            grow: 1,
            enableColumnFilter: true,
            filterVariant: 'text',
          }}
          enableRowSelection
          onRowSelectionChange={setRowSelection}
          state={tableState}
          enableColumnActions
          enableColumnResizing
          enableDensityToggle
          enableFullScreenToggle
          enableHiding
          enableGlobalFilter
          enableColumnFilters
          enableFilterMatchHighlighting
          positionToolbarAlertBanner="none"
          getRowId={(row) => row.jobId}
          initialState={{
            density: 'compact',
            pagination: { pageSize: 25, pageIndex: 0 },
            sorting: [{ id: 'jobId', desc: false }],
            showGlobalFilter: true,
          }}
          renderTopToolbarCustomActions={({ table }) => {
            const isNoData = tableData.length === 0
            const isNoPageData = tableData.length > 0
            return (
              <ExportCsv
                columns={exportColumns}
                isDisabled={!table.getIsSomeRowsSelected()}
                isNoData={isNoData}
                isNoPageData={isNoPageData}
                table={table}
              />
            )
          }}
          muiSearchTextFieldProps={{
            placeholder: t('routeTitles.qualityHubSearchPlaceholder'),
            sx: { minWidth: '280px' },
            variant: 'outlined',
            size: 'small',
          }}
          muiTablePaperProps={{
            elevation: 1,
            sx: { width: '100%', maxWidth: '100%', overflow: 'hidden' },
          }}
          muiTableProps={{
            sx: { width: '100%', tableLayout: 'fixed' },
          }}
          muiTableContainerProps={{
            sx: {
              maxHeight: 'min(70vh, 640px)',
              width: '100%',
              maxWidth: '100%',
              overflowY: 'auto',
              overflowX: 'hidden',
              backgroundColor: theme.palette?.background?.paper || '#fff',
            },
          }}
          muiTableBodyProps={{
            sx: {
              backgroundColor: theme.palette?.background?.paper || '#fff',
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              fontWeight: 700,
              backgroundColor: theme.palette?.grey?.[50] || '#f5f5f5',
              color: theme.palette?.text?.primary,
              zIndex: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              color: theme.palette?.text?.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              verticalAlign: 'middle',
            },
          }}
          muiTopToolbarProps={{
            sx: {
              flexWrap: 'wrap',
              gap: 1,
              py: 1,
              px: 1,
              maxWidth: '100%',
              overflowX: 'hidden',
              '& .MuiTextField-root': { maxWidth: 'min(100%, 320px)' },
            },
          }}
          muiTableBodyRowProps={({ staticRowIndex }) => {
            const paperBg = theme.palette?.background?.paper || '#fff'
            return {
              sx: {
                backgroundColor: paperBg,
              },
            }
          }}
        />
      </TableContainer>
    </MainRouteSubRoutePage>
  )
}

export default QualityHubJobs
